import React, { useState, useEffect } from "react";
import { ArrowRight, Sparkles, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getIcon } from "../utils/iconResolver";
import data from "../data/retailData.json";

const { palette: P, hero } = data;

const RetailHero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    const iv = setInterval(() => setCurrentSlide(p => (p + 1) % hero.carouselImages.length), 5500);
    return () => clearInterval(iv);
  }, []);

  const handleButtonClick = (btn) => {
    if (btn.scrollTo) {
      document.getElementById(btn.scrollTo)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (btn.href) {
      nav(btn.href);
    }
  };

  return (
    <section style={{
      position: "relative", minHeight: "100vh", display: "flex", alignItems: "center",
      background: P.darkBg2, overflow: "hidden",
    }}>
      {/* Carousel images */}
      {hero.carouselImages.map((img, i) => (
        <div key={i} style={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center",
          opacity: i === currentSlide ? 1 : 0, transition: "opacity 1.5s ease",
        }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(105deg, rgba(14,10,6,0.82) 0%, rgba(18,13,8,0.65) 55%, rgba(14,10,6,0.78) 100%)" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "45%", background: "linear-gradient(to top, rgba(14,10,6,0.95), transparent)" }} />
        </div>
      ))}

      {/* Glows */}
      <div style={{ position: "absolute", top: "-5%", right: "-4%", width: "clamp(300px, 45vw, 580px)", height: "clamp(300px, 45vw, 580px)", borderRadius: "50%", background: "radial-gradient(circle, rgba(210,140,50,0.13) 0%, transparent 65%)", zIndex: 1 }} />
      <div style={{ position: "absolute", bottom: "5%", left: "-3%", width: "clamp(200px, 30vw, 400px)", height: "clamp(200px, 30vw, 400px)", borderRadius: "50%", background: "radial-gradient(circle, rgba(93,162,162,0.08) 0%, transparent 65%)", zIndex: 1 }} />

      {/* Content */}
      <div style={{ position: "relative", zIndex: 2, maxWidth: 1280, margin: "0 auto", padding: "clamp(90px, 12vw, 110px) clamp(20px, 4vw, 48px) clamp(70px, 10vw, 90px)", width: "100%" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 400px), 1fr))",
          gap: "clamp(40px, 6vw, 80px)",
          alignItems: "center"
        }}>
          {/* LEFT */}
          <div>
            {/* Badge */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(93,162,162,0.12)", border: "1px solid rgba(93,162,162,0.30)", padding: "8px 20px", borderRadius: 100, marginBottom: 36 }}>
              <Sparkles style={{ width: 13, height: 13, color: P.tealLight }} />
              <span style={{ color: P.tealLight, fontSize: "clamp(10px, 1.5vw, 12px)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>{hero.badge}</span>
            </div>

            {/* Headline */}
            <h1 style={{ fontSize: "clamp(42px, 6vw, 70px)", fontWeight: 900, lineHeight: 1.02, letterSpacing: "-0.03em", marginBottom: 26, color: "white" }}>
              <span style={{ display: "block", fontWeight: 400, fontSize: "clamp(20px, 3vw, 36px)", color: "rgba(255,255,255,0.52)", letterSpacing: "-0.01em", marginBottom: 6 }}>{hero.headline.pre}</span>
              <span style={{ display: "block" }}>{hero.headline.main}</span>
              <span style={{ display: "block", background: `linear-gradient(90deg, ${P.tealLight} 0%, ${P.teal} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>{hero.headline.accent}</span>
            </h1>

            <p style={{ fontSize: "clamp(15px, 2vw, 17px)", color: "rgba(255,255,255,0.50)", lineHeight: 1.78, maxWidth: 460, marginBottom: 44 }}>{hero.description}</p>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 44 }}>
              {hero.buttons.map((btn, i) => (
                i === 0 ? (
                  <button key={i} onClick={() => handleButtonClick(btn)}
                    style={{ background: "rgba(93,162,162,0.20)", color: "rgba(255,255,255,0.92)", border: "1px solid rgba(145,205,205,0.32)", padding: "clamp(12px, 2vw, 15px) clamp(24px, 3vw, 36px)", borderRadius: 10, fontSize: "clamp(13px, 1.5vw, 15px)", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 9, backdropFilter: "blur(8px)", transition: "all 0.25s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(93,162,162,0.34)"; e.currentTarget.style.borderColor = "rgba(145,205,205,0.55)"; e.currentTarget.style.color = "white"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(93,162,162,0.20)"; e.currentTarget.style.borderColor = "rgba(145,205,205,0.32)"; e.currentTarget.style.color = "rgba(255,255,255,0.92)"; e.currentTarget.style.transform = "none"; }}>
                    {btn.label} <ArrowRight style={{ width: 16, height: 16 }} />
                  </button>
                ) : (
                  <button key={i} onClick={() => handleButtonClick(btn)}
                    style={{ background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.78)", border: "1px solid rgba(255,255,255,0.18)", padding: "clamp(12px, 2vw, 15px) clamp(24px, 3vw, 36px)", borderRadius: 10, fontSize: "clamp(13px, 1.5vw, 15px)", fontWeight: 600, cursor: "pointer", transition: "all 0.25s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "white"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = "rgba(255,255,255,0.78)"; }}>
                    {btn.label}
                  </button>
                )
              ))}
            </div>

            {/* Trust dots */}
            <div style={{ display: "flex", gap: "clamp(16px, 3vw, 28px)", flexWrap: "wrap" }}>
              {hero.trustPoints.map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: t.dot, flexShrink: 0 }} />
                  <span style={{ color: "rgba(255,255,255,0.38)", fontSize: "clamp(11px, 1.5vw, 13px)" }}>{t.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — feature cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {hero.featureCards.map((card, i) => (
              <div key={i} onClick={() => nav("/contact")}
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 14, padding: "clamp(14px, 2vw, 17px) clamp(16px, 2.5vw, 20px)", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", backdropFilter: "blur(8px)", transition: "all 0.25s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(93,162,162,0.45)"; e.currentTarget.style.background = "rgba(93,162,162,0.10)"; e.currentTarget.style.transform = "translateX(4px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.transform = "none"; }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 44, height: 44, background: P.teal, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", color: "white", flexShrink: 0, boxShadow: "0 4px 14px rgba(93,162,162,0.35)" }}>
                    {getIcon(card.icon, { style: { width: 20, height: 20 } })}
                  </div>
                  <div>
                    <div style={{ color: "rgba(255,255,255,0.92)", fontWeight: 700, fontSize: "clamp(13px, 1.5vw, 14px)" }}>{card.label}</div>
                    <div style={{ color: "rgba(255,255,255,0.40)", fontSize: "clamp(11px, 1.3vw, 12.5px)", marginTop: 2 }}>{card.description}</div>
                  </div>
                </div>
                <ChevronRight style={{ width: 15, height: 15, color: "rgba(255,255,255,0.28)", flexShrink: 0 }} />
              </div>
            ))}

            <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "13px 18px", textAlign: "center" }}>
              <p style={{ color: "rgba(255,255,255,0.26)", fontSize: 12, marginBottom: 9 }}>And much more included in every plan</p>
              <div style={{ display: "flex", gap: 7, justifyContent: "center", flexWrap: "wrap" }}>
                {hero.extraTags.map(tag => (
                  <span key={tag} style={{ background: "rgba(93,162,162,0.15)", color: P.tealLight, fontSize: 11, padding: "4px 11px", borderRadius: 100, fontWeight: 600 }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide dots */}
      <div style={{ position: "absolute", bottom: 34, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 8, zIndex: 10 }}>
        {hero.carouselImages.map((_, i) => (
          <button key={i} onClick={() => setCurrentSlide(i)}
            style={{ width: i === currentSlide ? 28 : 8, height: 8, borderRadius: 4, border: "none", cursor: "pointer", transition: "all 0.3s", background: i === currentSlide ? P.teal : "rgba(255,255,255,0.24)" }} />
        ))}
      </div>
    </section>
  );
};

export default RetailHero;